import React from 'react';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Book, Award, Globe, Users, Heart, MessageCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO, createOrganizationSchema, createBreadcrumbSchema, createLocalBusinessSchema } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';
import AnimateOnScroll, { StaggerContainer, StaggerItem } from '@/components/common/AnimateOnScroll';

const AboutPage = () => {
  const { language, isRTL, t } = useLanguage();

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "url": "https://hudanlilmuttaqeen.com/about",
        "name": isRTL ? "عن هُدًى للمتقين" : "About Hudan Lil Muttaqeen",
        "inLanguage": isRTL ? "ar" : "en"
      },
      createOrganizationSchema(language),
      createLocalBusinessSchema({ language }),
      createBreadcrumbSchema([
        { name: t('nav.home'), url: 'https://hudanlilmuttaqeen.com/' },
        { name: t('nav.about'), url: 'https://hudanlilmuttaqeen.com/about' }
      ])
    ]
  };

  const values = [
    {
      icon: Book,
      titleAr: 'العلم والإتقان',
      titleEn: 'Knowledge & Excellence',
      descAr: 'نلتزم بتوفير أعلى جودة في تعليم القرآن الكريم مع معلمين مؤهلين ومعتمدين.',
      descEn: 'We are committed to providing the highest quality Quranic education with knowledgeable and certified teachers.'
    },
    {
      icon: Award,
      titleAr: 'الأصالة',
      titleEn: 'Authenticity',
      descAr: 'نضمن أن جميع التعاليم مبنية على مصادر موثوقة ومنهجيات تقليدية مع تبني تقنيات التعليم الحديثة.',
      descEn: 'We ensure that all teachings are based on authentic sources and traditional methodologies while embracing modern teaching techniques.'
    },
    {
      icon: Users,
      titleAr: 'المجتمع',
      titleEn: 'Community',
      descAr: 'نعزز مجتمعًا داعمًا من المتعلمين والمعلمين، مما يخلق بيئة من الاحترام والتعاون المتبادل.',
      descEn: 'We foster a supportive community of learners and teachers, creating an environment of mutual respect and cooperation.'
    },
    {
      icon: Globe,
      titleAr: 'سهولة الوصول',
      titleEn: 'Accessibility',
      descAr: 'نؤمن بجعل تعليم القرآن متاحًا للجميع، بغض النظر عن الموقع أو الخلفية أو المعرفة السابقة.',
      descEn: 'We believe in making Quranic education accessible to everyone, regardless of location, background, or prior knowledge.'
    },
    {
      icon: Heart,
      titleAr: 'الرحمة',
      titleEn: 'Compassion',
      descAr: 'نتعامل مع التعليم بصبر وتفهم ورعاية، مع إدراك أن لكل طالب احتياجات وأساليب تعلم فريدة.',
      descEn: 'We approach education with patience, understanding, and care, recognizing that each student has unique needs and learning styles.'
    },
    {
      icon: MessageCircle,
      titleAr: 'التواصل',
      titleEn: 'Communication',
      descAr: 'نعطي الأولوية للتواصل الواضح والمفتوح بين المعلمين والطلاب وأولياء الأمور لضمان تجربة تعليمية إيجابية.',
      descEn: 'We prioritize clear and open communication between teachers, students, and parents to ensure a positive learning experience.'
    }
  ];

  const team = [
    {
      nameAr: 'د. عبدالله الرحمن', nameEn: 'Dr. Abdullah Rahman',
      positionAr: 'المؤسس والمدير', positionEn: 'Founder & Director',
      bioAr: 'أسس منصة هُدًى للمتقين برؤية لجعل تعليم القرآن الأصيل متاحًا للمسلمين في جميع أنحاء العالم. يحمل درجة الدكتوراه في الدراسات الإسلامية ولديه أكثر من 20 عامًا من الخبرة.',
      bioEn: 'Founded Hudan Lil Muttaqeen with a vision to make authentic Quranic education accessible worldwide. Holds a PhD in Islamic Studies with over 20 years of teaching experience.'
    },
    {
      nameAr: 'د. أمينة خالد', nameEn: 'Dr. Aminah Khalid',
      positionAr: 'المديرة الأكاديمية', positionEn: 'Academic Director',
      bioAr: 'تشرف على تطوير المناهج وجودة التعليم. بخلفيتها في علم النفس التربوي والدراسات الإسلامية، تضمن فعالية طرق التدريس.',
      bioEn: 'Oversees curriculum development and education quality. Background in educational psychology and Islamic studies ensures effective teaching methods.'
    },
    {
      nameAr: 'أ. محمد علي', nameEn: 'Ustadh Muhammad Ali',
      positionAr: 'منسق تدريب المعلمين', positionEn: 'Teacher Training Coordinator',
      bioAr: 'يحمل عدة إجازات في تلاوة القرآن ومكرس للحفاظ على أعلى المعايير لمعلمينا.',
      bioEn: 'Holds multiple ijazat in Quran recitation and is dedicated to maintaining the highest standards for our teachers.'
    },
    {
      nameAr: 'سارة جونسون', nameEn: 'Sarah Johnson',
      positionAr: 'مديرة العمليات', positionEn: 'Operations Manager',
      bioAr: 'تضمن سلاسة العمليات اليومية وتنسق شبكتنا العالمية من المعلمين والطلاب بكفاءة.',
      bioEn: 'Ensures smooth day-to-day operations and coordinates our global network of teachers and students efficiently.'
    }
  ];

  return (
    <Layout>
      <SEO
        titleKey="seo.about.title"
        descriptionKey="seo.about.description"
        keywordsKey="seo.about.keywords"
        structuredData={structuredData}
      />

      <PageHeader
        title={isRTL ? 'عن هُدًى للمتقين' : 'About Hudan Lil Muttaqeen'}
        subtitle={isRTL ? 'منصة عالمية مكرسة لربط الطلاب بمعلمي القرآن المؤهلين والمعتمدين' : 'A global platform dedicated to connecting students with qualified, Ijazah-certified Quran teachers'}
        pattern="geometric"
      />

      {/* Our Story */}
      <section className="section bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">
          <AnimateOnScroll>
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-3">
                {isRTL ? 'قصتنا' : 'Our Story'}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                {isRTL ? 'من رؤية إلى واقع' : 'From Vision to Reality'}
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  {isRTL
                    ? 'بدأت هُدًى للمتقين برؤية بسيطة: سد الفجوة بين معلمي القرآن المؤهلين والطلاب المتحمسين عبر العالم. أدرك مؤسسنا التحديات التي يواجهها العديد من المسلمين في الوصول إلى تعليم قرآني عالي الجودة.'
                    : 'Hudan Lil Muttaqeen began with a simple vision: to bridge the gap between qualified Quran teachers and eager students across the globe. Our founder recognized the challenges many Muslims face in accessing quality Quranic education.'}
                </p>
                <p>
                  {isRTL
                    ? 'ما بدأ كمبادرة صغيرة نما ليصبح منصة عالمية تخدم الآلاف. اسمنا "هُدًى للمتقين" يعكس التزامنا بتوفير تعليم منير متجذر في التقاليد الإسلامية الأصيلة.'
                    : 'What started as a small initiative has grown into a global platform serving thousands. Our name, "Hudan Lil Muttaqeen" (Guidance for the God-conscious), reflects our commitment to enlightening education rooted in authentic Islamic tradition.'}
                </p>
              </div>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Values */}
      <section className="section bg-muted/30" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">
          <AnimateOnScroll className="text-center mb-14">
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-2">
              {isRTL ? 'قيمنا' : 'Our Values'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              {isRTL ? 'ما نؤمن به' : 'What We Believe In'}
            </h2>
          </AnimateOnScroll>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, i) => (
              <StaggerItem key={i}>
                <div className="bg-card rounded-xl border border-border p-6 h-full group hover:shadow-soft hover:border-primary/20 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                    <value.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {isRTL ? value.titleAr : value.titleEn}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {isRTL ? value.descAr : value.descEn}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Team */}
      <section className="section bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">
          <AnimateOnScroll className="text-center mb-14">
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-2">
              {isRTL ? 'فريقنا' : 'Our Team'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              {isRTL ? 'قيادة ملتزمة' : 'Dedicated Leadership'}
            </h2>
          </AnimateOnScroll>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {team.map((member, i) => (
              <StaggerItem key={i}>
                <div className="bg-card rounded-xl border border-border p-6 flex gap-5 group hover:shadow-soft hover:border-primary/20 transition-all duration-300">
                  <div
                    className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/15 transition-colors"
                    aria-hidden="true"
                  >
                    <span className="text-primary font-arabic text-2xl font-bold">
                      {(isRTL ? member.nameAr : member.nameEn).charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-0.5">
                      {isRTL ? member.nameAr : member.nameEn}
                    </h3>
                    <p className="text-sm font-medium text-primary mb-2">
                      {isRTL ? member.positionAr : member.positionEn}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {isRTL ? member.bioAr : member.bioEn}
                    </p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="section" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">
          <AnimateOnScroll>
            <div className="relative overflow-hidden rounded-2xl bg-hero-gradient dark:bg-hero-gradient-dark px-8 py-14 md:px-16 md:py-20">
              <div className="absolute inset-0 islamic-pattern-overlay opacity-40" />
              <div className="relative z-10 max-w-2xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {isRTL ? 'انضم إلى مجتمعنا القرآني' : 'Join Our Quranic Community'}
                </h2>
                <p className="text-emerald-100/80 text-lg mb-8">
                  {isRTL
                    ? 'سواء كنت تتطلع للتعلم أو التدريس، نرحب بك في مجتمعنا'
                    : "Whether you're looking to learn or teach, we welcome you to our community"}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    asChild
                    size="lg"
                    className="bg-amber-500 hover:bg-amber-500/90 text-amber-950 font-semibold rounded-xl px-8 h-12"
                  >
                    <Link to="/register" className="flex items-center gap-2">
                      {isRTL ? 'سجل كطالب' : 'Register as Student'}
                      <ArrowRight className="h-4 w-4 rtl-mirror" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    className="bg-transparent border border-white/30 text-white hover:bg-white/10 rounded-xl px-8 h-12"
                  >
                    <Link to="/register/teacher">
                      {isRTL ? 'انضم كمعلم' : 'Become a Teacher'}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </AnimateOnScroll>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
